/**
 * "Defend your score" — user-facing re-test page.
 *
 * State machine driven by `GET /retest-quiz`:
 *
 *   * null              → empty state ("no active re-test")
 *   * status='sent'     → intro screen (category, WAT deadline,
 *                         one-attempt warning, big "Start attempt" button)
 *   * status='in_progress' → the attempt itself (one question at a time,
 *                         countdown, shared hardened renderer, decoy trap,
 *                         client-integrity telemetry). Submit at the end.
 *   * status='submitted'→ neutral "under review" screen (NEVER shows the
 *                         score or the comparison band — that's admin-only)
 *   * status='expired'  → "This re-test expired on <date>" state.
 *
 * Rendering uses the SAME closed-shadow-DOM question view + decoy trap
 * that live-trivia and duels use (lib/question_view.js). This is the
 * most bot-resistant screen in the product — anything that renders as
 * ordinary DOM here defeats the whole point of the feature.
 *
 * Power-ups are intentionally NOT wired in (no 50/50, no eraser). The
 * spec says a re-test never mutates account_state, and power-ups exist
 * for the scoring surface only. The page runs entirely offline of the
 * scoring / payout code paths.
 */

import { ApiError } from '../api.js';
import { startCollector } from '../lib/client_signals.js';
import { escape } from '../components/form.js';
import { mountDecoyBlock, mountQuestionView } from '../lib/question_view.js';
import {
  formatCountdown,
  formatLagosDate,
  formatLagosDeadline,
  getActiveRetest,
  msRemainingUntil,
  startRetest,
  submitRetest,
} from '../lib/retest.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

// Timer redraw cadence during the attempt. Matches live_room / duel_run.
const TICK_MS = 250;
// Guard against phantom taps in the first ~350ms of a question mount.
const TAP_WARMUP_MS = 350;

export async function renderRetest(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Defend your score</div>
    </div>
    <div class="pad" id="retest-page">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;
  const page = document.getElementById('retest-page');

  let payload;
  try {
    payload = await getActiveRetest();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = errorCard(err);
    return;
  }

  if (payload == null) {
    page.innerHTML = emptyStateHtml();
    return;
  }

  await routeByStatus(page, payload);
}

// ---- State router ---------------------------------------------------------

async function routeByStatus(page, retest) {
  const status = retest.status;
  if (status === 'sent') {
    return renderIntro(page, retest);
  }
  if (status === 'in_progress') {
    return runAttempt(page, retest);
  }
  if (status === 'submitted') {
    return renderSubmitted(page, retest);
  }
  if (status === 'expired') {
    return renderExpired(page, retest);
  }
  // Defensive fallback — a status the frontend doesn't know shouldn't
  // brick the page; show the raw payload's copy area instead.
  page.innerHTML = `
    <div class="card" style="padding:16px;">
      <div style="font-family:var(--font-display);color:#fff;">Re-test status: ${escape(status)}</div>
      <div class="muted" style="font-size:12px;margin-top:6px;">The app doesn't know how to render this state yet. Check for updates.</div>
    </div>
  `;
}

// ---- Empty state ----------------------------------------------------------

function emptyStateHtml() {
  return `
    <div class="card" style="padding:20px;text-align:center;">
      <div style="font-size:32px;">🛡️</div>
      <div style="font-family:var(--font-display);font-size:16px;color:#fff;margin-top:8px;font-weight:600;">
        No re-test to take
      </div>
      <div class="muted" style="font-size:13px;margin-top:8px;line-height:1.5;">
        A re-test appears here if an admin asks you to defend a recent score.
        You'll get a notification the moment one is issued.
      </div>
      <a href="/" data-link class="btn primary" style="display:inline-block;margin-top:16px;padding:9px 18px;text-decoration:none;">
        Back to home
      </a>
    </div>
  `;
}

// ---- Intro screen (status=sent) -------------------------------------------

function renderIntro(page, retest) {
  const deadlineText = formatLagosDeadline(retest.deadline_at);
  const windowMinutes = Math.round(Number(retest.window_s || 180) / 60);
  const windowLabel = windowMinutes >= 1
    ? `${windowMinutes} minute${windowMinutes === 1 ? '' : 's'}`
    : `${retest.window_s} seconds`;
  page.innerHTML = `
    <div class="card" style="padding:20px;">
      <div style="display:inline-block;padding:4px 10px;border-radius:999px;background:rgba(251,191,36,.14);border:1px solid rgba(251,191,36,.4);color:#FBBF24;font-family:var(--font-display);font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;">
        Adjudication
      </div>
      <div style="font-family:var(--font-display);font-size:20px;color:#fff;margin-top:12px;font-weight:600;">
        Defend your score
      </div>
      <div class="muted" style="font-size:13.5px;margin-top:10px;line-height:1.6;">
        An admin has asked you to answer a fresh set of <b style="color:#fff;">${escape(String(retest.n_total))}</b> questions
        from the same category as one of your recent games. Someone who really
        knows the topic scores comparably on new questions — that's the point.
      </div>

      <div style="margin-top:18px;padding:14px 15px;background:rgba(233,75,162,0.10);border:1px solid rgba(233,75,162,0.4);border-radius:10px;">
        <div style="font-family:var(--font-display);font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:#E94BA2;">
          Complete by
        </div>
        <div style="font-family:var(--font-display);font-size:15px;color:#fff;margin-top:5px;font-weight:600;">
          ${escape(deadlineText)}
        </div>
      </div>

      <ul style="margin:18px 0 0;padding:0;list-style:none;font-size:12.5px;line-height:1.7;color:#fff;">
        <li>⏱️ One attempt only. Once you start, the ${escape(windowLabel)} timer runs down.</li>
        <li>🛑 Timer stops at the deadline above — even if you had window left.</li>
        <li>📝 One question at a time. Server grades all answers when you submit.</li>
        <li>👀 An admin reviews your answers alongside the flagged game.</li>
      </ul>

      <div style="margin-top:16px;padding:12px 14px;background:rgba(139,107,242,.08);border:1px dashed rgba(139,107,242,.35);border-radius:10px;font-size:12px;color:var(--mute);line-height:1.55;">
        You cannot pause or restart. Make sure your device is charged and you
        have a stable connection before you begin.
      </div>

      <button type="button" id="retest-start-btn" class="btn primary" style="margin-top:20px;width:100%;padding:14px;font-size:15px;">
        Start attempt
      </button>
    </div>
  `;
  const btn = page.querySelector('#retest-start-btn');
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = 'Loading…';
    let started;
    try {
      started = await startRetest(retest.id);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Start attempt';
      if (err instanceof ApiError && err.code === 'retest_expired') {
        toast.error('This re-test expired before you could start it.');
        // Re-fetch to move to the expired state.
        return renderRetest(document.getElementById('route-shell'));
      }
      toast.fromError ? toast.fromError(err) : toast.error(err.message || 'Could not start re-test.');
      return;
    }
    // `started` has: id, status='in_progress', started_at, effective_end_at,
    // window_s, deadline_at, questions[] (no answer_index)
    await runAttempt(page, started);
  });
}

// ---- Attempt loop (status=in_progress) ------------------------------------

async function runAttempt(page, retest) {
  const questions = Array.isArray(retest.questions) ? retest.questions : [];
  if (questions.length === 0) {
    page.innerHTML = `
      <div class="card" style="padding:16px;color:var(--pink-t);">
        This re-test has no questions to serve. Ask the admin to re-issue.
      </div>
    `;
    return;
  }

  const effectiveEndIso = retest.effective_end_at || retest.deadline_at;
  const state = {
    retestId: retest.id,
    total: questions.length,
    cursor: 0,
    answers: [],           // one entry per question in order
    effectiveEndIso,
    // Held for the current question only. Rebuilt each question so
    // ms_since_render + visibility_changes reflect THIS question's
    // observation window.
    collector: null,
    // Latest DOM event on the current question's pick; snapshotted for
    // client-signals is_trusted / pointer_type / pointer geometry.
    picked: null,
  };

  await questionLoop(page, questions, state);
}

async function questionLoop(page, questions, state) {
  while (state.cursor < state.total) {
    const q = questions[state.cursor];
    const answer = await runOneQuestion(page, q, state);
    state.answers.push(answer);
    state.cursor += 1;
    // If the deadline (not just the per-question timer) elapsed while
    // rendering the question, submit whatever we have and let the
    // server return the expired verdict.
    if (msRemainingUntil(state.effectiveEndIso) <= 0) break;
  }
  await submitAll(page, state);
}

async function runOneQuestion(page, question, state) {
  const startedAt = performance.now();
  let selectedIndex = null;
  let picked = false;
  let clientSignals = null;
  const collector = startCollector({ questionRenderedAt: startedAt });

  paintAnswering(page, question, state);
  const host = page.querySelector('#retest-qv-host');

  const view = mountQuestionView(host, {
    mode: 'live',
    onSelect: (idx, ev) => {
      if (picked) return;
      if (performance.now() - startedAt < TAP_WARMUP_MS) return;
      picked = true;
      selectedIndex = idx;
      // Snapshot BEFORE lockAnswer so client_signals reflect the state
      // at the moment of click, not after the optimistic-lock repaint.
      clientSignals = collector.snapshot({ answerEvent: ev });
      view.lockAnswer(idx);
    },
  });
  view.setQuestion({
    prompt: question.prompt,
    choices: question.choices,
    twoColumnLayout: !!question.image_url,
  });

  // Decoy trap — light-DOM siblings of the shadow host. Any click on
  // one of these is a strong screen-scraper tell (a human cannot see
  // the transparent decoy). The decoy click never submits an answer;
  // the real answer path lives inside the closed shadow root.
  const slot = host.parentElement;
  if (slot) {
    mountDecoyBlock(slot, {
      onDecoyHit: () => collector.markDecoyHit(),
    });
  }

  // Tick the countdown against the EFFECTIVE END (deadline OR
  // started_at+window_s, whichever is nearer). The server-side
  // effective_end_at is authoritative — we trust its value rather than
  // deriving locally.
  await new Promise((resolve) => {
    const tick = () => {
      const remaining = msRemainingUntil(state.effectiveEndIso);
      updateTimer(page, remaining);
      if (picked || remaining <= 0) {
        clearInterval(t);
        resolve();
      }
    };
    tick();
    const t = setInterval(tick, TICK_MS);
  });

  // Timed-out without a pick — drain the collector for whatever
  // observability signals it gathered.
  if (clientSignals === null) {
    clientSignals = collector.snapshot({ answerEvent: null });
  }

  const ms = Math.max(0, Math.round(performance.now() - startedAt));
  return {
    // The server keys grading by question order — which is the index
    // into the served `questions` array (also stored on the backend
    // row). `state.cursor` == this question's index, since the loop
    // advances the cursor after this returns.
    order: state.cursor,
    choice_index: picked ? selectedIndex : null,
    ms,
    signals: clientSignals,
  };
}

async function submitAll(page, state) {
  page.innerHTML = `
    <div class="card center" style="padding:22px;">
      <div style="font-family:var(--font-display);font-size:16px;color:#fff;font-weight:600;margin-bottom:6px;">
        Submitting your answers…
      </div>
      <div class="muted" style="font-size:12.5px;">Scoring is server-side. This only takes a moment.</div>
    </div>
  `;
  try {
    const res = await submitRetest(state.retestId, state.answers);
    // `res` shape: { id, status, submitted_at, message }
    // Deliberately neutral — no score, no comparison band. The user
    // learns the outcome via a direct message from the admin.
    renderSubmitted(page, {
      id: res.id,
      status: res.status || 'submitted',
      submitted_at: res.submitted_at,
      // Best-effort deadline info for the "submitted before X" copy:
      deadline_at: null,
    });
  } catch (err) {
    if (err instanceof ApiError && err.code === 'retest_expired') {
      // Server has already flipped the row; refresh the whole page to
      // land the user on the expired copy.
      toast.error('This re-test expired before you finished.');
      return renderRetest(document.getElementById('route-shell'));
    }
    if (err instanceof ApiError && err.code === 'already_submitted') {
      // Race: another tab already submitted. Show the neutral submitted
      // state either way — the point is that they're done.
      return renderSubmitted(page, { id: state.retestId, status: 'submitted', submitted_at: null });
    }
    page.innerHTML = `
      <div class="card" style="padding:16px;color:var(--pink-t);">
        <div style="font-family:var(--font-display);font-size:14px;font-weight:600;color:#fff;">Submit failed</div>
        <div class="muted" style="font-size:12.5px;margin-top:6px;">${escape(err.message || 'Try again.')}</div>
        <button type="button" id="retest-retry-submit" class="btn primary" style="margin-top:14px;padding:10px 16px;">
          Try again
        </button>
      </div>
    `;
    page.querySelector('#retest-retry-submit').addEventListener('click', () => submitAll(page, state));
  }
}

// ---- Paint helpers --------------------------------------------------------

function paintAnswering(page, question, state) {
  const num = state.cursor + 1;
  const total = state.total;
  const remainingMs = msRemainingUntil(state.effectiveEndIso);
  const startPct = 100;  // repainted by updateTimer on first tick
  page.innerHTML = `
    <div class="card" style="padding:14px;">
      <div class="row" style="align-items:center;">
        <span class="muted" style="font-size:11px;letter-spacing:0.05em;text-transform:uppercase;font-weight:700;">
          Question ${escape(String(num))} of ${escape(String(total))}
        </span>
        <div class="sp"></div>
        <span id="retest-timer" style="font-family:var(--font-display);font-size:14px;color:#fff;font-weight:600;">
          ${escape(formatCountdown(remainingMs))}
        </span>
      </div>
      <div style="height:4px;background:#1a1432;border-radius:3px;margin:8px 0 12px;overflow:hidden;">
        <div id="retest-progress-bar"
             style="height:100%;width:${startPct}%;background:linear-gradient(90deg,#5B2C91,#E94BA2);transition:width ${TICK_MS}ms linear;">
        </div>
      </div>
      ${question.image_url
        ? `<img src="${escape(question.image_url)}" alt=""
             style="width:100%;border-radius:10px;margin:6px 0 10px;object-fit:cover;max-height:220px;" />`
        : ''}
      <!-- Prompt + option grid render inside a closed shadow root — see
           lib/question_view.js. This is the same hardened renderer used
           by live trivia and duels. The qv-slot wrapper is
           position:relative so mountDecoyBlock can position its
           light-DOM decoys absolutely on top of the shadow host. -->
      <div class="qv-slot" style="position:relative;">
        <div id="retest-qv-host" style="position:relative;z-index:1;"></div>
      </div>
    </div>
    <div class="muted" style="font-size:11.5px;margin:12px 4px 0;text-align:center;">
      One shot per question. No 50/50, no eraser on a re-test.
    </div>
  `;
}

function updateTimer(page, remainingMs) {
  const timer = page.querySelector('#retest-timer');
  const bar = page.querySelector('#retest-progress-bar');
  if (timer) timer.textContent = formatCountdown(remainingMs);
  if (bar) {
    // The bar shrinks proportionally to how close we are to the
    // effective end. We anchor at "however much time we had when this
    // question mounted" — recomputed here from the start of the tick.
    // Rather than tracking per-question windows, we shrink linearly
    // from 100% → 0% over the remaining time; keeps the code simple.
    // Cap at 30 minutes visually so a very long window doesn't crawl.
    const pct = Math.max(0, Math.min(100, (remainingMs / (30 * 60 * 1000)) * 100));
    bar.style.width = `${pct}%`;
  }
}

// ---- Submitted state ------------------------------------------------------

function renderSubmitted(page, retest) {
  page.innerHTML = `
    <div class="card" style="padding:22px;text-align:center;">
      <div style="font-size:34px;">📬</div>
      <div style="font-family:var(--font-display);font-size:17px;color:#fff;margin-top:10px;font-weight:600;">
        Answers submitted
      </div>
      <div class="muted" style="font-size:13px;margin-top:10px;line-height:1.6;">
        Your answers are in. An admin will review them alongside the flagged game
        and follow up directly. Your account status is unchanged by this submission.
      </div>
      <a href="/" data-link class="btn primary" style="display:inline-block;margin-top:18px;padding:9px 18px;text-decoration:none;">
        Back to home
      </a>
    </div>
  `;
}

// ---- Expired state --------------------------------------------------------

function renderExpired(page, retest) {
  const dateText = retest.deadline_at ? formatLagosDate(retest.deadline_at) : 'the deadline';
  page.innerHTML = `
    <div class="card" style="padding:20px;text-align:center;">
      <div style="font-size:32px;">⏰</div>
      <div style="font-family:var(--font-display);font-size:16px;color:#fff;margin-top:10px;font-weight:600;">
        This re-test expired
      </div>
      <div class="muted" style="font-size:13px;margin-top:10px;line-height:1.6;">
        The deadline was <b style="color:#fff;">${escape(dateText)}</b>. The admin will
        follow up with next steps if any are needed.
      </div>
      <a href="/" data-link class="btn primary" style="display:inline-block;margin-top:18px;padding:9px 18px;text-decoration:none;">
        Back to home
      </a>
    </div>
  `;
}

// ---- Error card -----------------------------------------------------------

function errorCard(err) {
  return `
    <div class="card" style="padding:16px;color:var(--pink-t);">
      <div style="font-family:var(--font-display);font-size:14px;font-weight:600;color:#fff;">Could not load</div>
      <div class="muted" style="font-size:12.5px;margin-top:6px;">${escape(err.message || 'Try again in a moment.')}</div>
    </div>
  `;
}
